import { useState } from 'react';
import { Interaction, useUpdateInteraction } from '../api';
import { INTERACTION_TYPES, formatInteractionType } from '../interactionTypes';

function localDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  interaction: Interaction;
  onDone: () => void;
  onCancel: () => void;
}

export default function InteractionEditForm({ interaction, onDone, onCancel }: Props) {
  const initialDate = localDate(interaction.occurred_at);
  const [date, setDate] = useState(initialDate);
  const [type, setType] = useState(interaction.type);
  const [performedBy, setPerformedBy] = useState(interaction.performed_by ?? '');
  const [notes, setNotes] = useState(interaction.notes ?? '');
  const update = useUpdateInteraction();

  const save = () => {
    const body: Parameters<typeof update.mutate>[0] = { id: interaction.id };
    // Only send what changed; an untouched date keeps the stored timestamp.
    if (date && date !== initialDate) body.occurred_at = date;
    if (type !== interaction.type) body.type = type;
    if (performedBy.trim() !== (interaction.performed_by ?? '')) body.performed_by = performedBy.trim();
    if (notes.trim() !== (interaction.notes ?? '').trim()) body.notes = notes.trim();
    if (Object.keys(body).length === 1) { onCancel(); return; }
    update.mutate(body, { onSuccess: onDone });
  };

  return (
    <div className="space-y-2 py-1">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          Date
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          />
        </label>
        <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
          >
            {INTERACTION_TYPES.map((t) => (
              <option key={t} value={t}>{formatInteractionType(t)}</option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
        Performed by
        <input
          value={performedBy}
          onChange={(e) => setPerformedBy(e.target.value)}
          placeholder="Leave blank if it was you"
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
        />
      </label>
      <label className="flex flex-col gap-0.5 text-xs text-slate-600 dark:text-slate-400">
        Notes
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm"
        />
      </label>
      {update.error && (
        <p className="text-xs text-rose-700 dark:text-rose-300">
          Save failed: {(update.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-1">
        <button
          onClick={save}
          disabled={update.isPending || !date}
          className="rounded bg-slate-700 dark:bg-slate-600 px-3 py-1 text-xs text-white disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={update.isPending}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs text-slate-600 dark:text-slate-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
